import {
  DEFAULT_SHORTCUTS,
  findActionFor,
  type SessionAction,
  type ShortcutMap
} from './keys';

export interface KeyboardOptions {
  shortcuts?: ShortcutMap;
  onAction: (action: SessionAction, event: KeyboardEvent) => void;
  target?: Window | HTMLElement;
}

function isEditableElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    tag === 'A'
  ) {
    return true;
  }
  if ((el as HTMLElement).isContentEditable === true) return true;
  const role = el.getAttribute('role');
  if (role === 'button' || role === 'link' || role === 'menuitem') return true;
  return false;
}

export function attachKeyboard(options: KeyboardOptions): () => void {
  const shortcuts = options.shortcuts ?? DEFAULT_SHORTCUTS;
  const target = options.target ?? window;

  const keysFromInput = new Set<string>();

  const downHandler = (event: Event) => {
    const e = event as KeyboardEvent;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (isEditableElement(document.activeElement)) {
      keysFromInput.add(e.key);
      return;
    }
    const action = findActionFor(shortcuts, e.key);
    if (!action) return;
    e.preventDefault();
  };

  const upHandler = (event: Event) => {
    const e = event as KeyboardEvent;
    if (keysFromInput.has(e.key)) {
      keysFromInput.delete(e.key);
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (isEditableElement(document.activeElement)) return;
    const action = findActionFor(shortcuts, e.key);
    if (!action) return;
    e.preventDefault();
    options.onAction(action, e);
  };

  target.addEventListener('keydown', downHandler);
  target.addEventListener('keyup', upHandler);
  return () => {
    target.removeEventListener('keydown', downHandler);
    target.removeEventListener('keyup', upHandler);
  };
}